import { http } from '../jobs/api/http.js';
export async function fetchOrders() {
  const res = await http.get('/api/redis/orders');
  return res.data.orders;
}

export async function fetchOrder(orderId) {
  const res = await http.get(`/api/redis/orders/${orderId}`);
  console.log('RES ? ', res);
  return res.data.order;
}

export async function fetchOpenLines(params = {}) {
  // params: { client, from, to }
  const res = await http.get('/api/redis/orders/open-lines', { params });

  return res.data.lines; // { success:true, lines:[...] }
}
export async function fetchJobOrdersSummary(jobId) {
  const { data } = await http.get(`/api/redis/jobs/${jobId}/orders-summary`);
  return data;
}

// ── lifecycle ───────────────────────────────────────────────────────────────
// Cancelling a line and deleting an order both release what they had claimed:
// the products allocated to them give up the claim, which is what puts their
// cable back in stock, and the warehouse frees any drums it was holding.

export async function cancelOrderLine(orderId, lineId, reason) {
  const { data } = await http.post(
    `/api/redis/orders/${orderId}/lines/${lineId}/cancel`,
    { reason }
  );
  return data;
}

export async function deleteOrder(orderId, reason) {
  const { data } = await http.delete(`/api/redis/orders/${orderId}`, {
    data: { reason }
  });
  return data;
}

export async function fetchArchivedOrders(limit = 100) {
  const { data } = await http.get('/api/redis/orders/archived', { params: { limit } });
  return data;
}

/** Cancel `qty` units of one delivery-plan item — a drum, forty coils. */
export async function cancelPlanUnits(orderId, lineId, itemId, qty, reason) {
  const { data } = await http.post(
    `/api/redis/orders/${orderId}/lines/${lineId}/items/${itemId}/cancel`,
    { qty, reason }
  );
  return data;
}

// ── Token-based feasibility and reservation ───────────────────────────────
//
// The metres view answers "is there enough cable". These answer "is there cable
// in the right LENGTHS", which is a different question and the only one the
// factory floor can act on: one 15,000 m drum cannot be cut out of four drums
// of 5,000, however the metres add up.

/** Per delivery item: what can actually be made, and off which drums. */
export async function fetchTokenFeasibility(orderId) {
  const { data } = await http.get(
    `/api/redis/orders/${orderId}/token-feasibility`
  );
  return data;
}

/**
 * Promise particular drums to a delivery item.
 * @param picks [{ jobId, tokenId, units }] — tokenId may be "A+B+C" for a set
 *              of cores that will be stranded into one cable.
 */
export async function reserveTokensForItem(orderId, lineId, itemId, picks) {
  const { data } = await http.post(
    `/api/redis/orders/${orderId}/lines/${lineId}/items/${itemId}/reserve-tokens`,
    { picks }
  );
  return data;
}

/** Give one drum back to stock. */
export async function releaseTokensForItem(orderId, lineId, itemId, jobId, tokenId) {
  const { data } = await http.post(
    `/api/redis/orders/${orderId}/lines/${lineId}/items/${itemId}/release-tokens`,
    { jobId, tokenId }
  );
  return data;
}

/**
 * Feasibility for many orders in one request.
 *
 * The screen asks about every open line at once. One request per order made the
 * backend re-read every job in the factory each time — identical work, repeated
 * as many times as there were orders on the page.
 */
export async function fetchTokenFeasibilityBatch(orderIds) {
  const { data } = await http.post('/api/redis/orders/token-feasibility', {
    orderIds
  });
  return data;
}

/**
 * The same question from the product's side: for each launched product, which
 * orders could take its cable and how much is left to give.
 *
 * The planner's axis. Order-centric answers "can this order be filled"; this
 * answers "where does this cable go", which is the decision actually being made
 * when a product comes off a machine with metres to spare.
 */
export async function fetchProductAllocation(jobIds = null) {
  const { data } = await http.post('/api/redis/orders/product-allocation', {
    ...(jobIds ? { jobIds } : {})
  });
  return data;
}

/**
 * Every order the factory has ever taken, in one shape.
 *
 * Open orders live in Redis, delivered ones in `CompletedOrder`, deleted ones in
 * a raw Mongo collection. Three stores means three shapes, and any screen that
 * wanted the whole history had to know all three existed. The backend folds
 * them; this just asks.
 */
export async function fetchOrdersAnalytics(params = {}) {
  const { data } = await http.get('/api/redis/orders/analytics', { params });
  return data;
}

/** Delivered orders, newest first, one page at a time. See fetchCompletedJobs. */
export async function fetchCompletedOrders({ page = 1, limit = 25, q = '' } = {}) {
  const { data } = await http.get('/api/redis/orders/completed', {
    params: { page, limit, ...(q ? { q } : {}) }
  });
  return data;
}
